import React, {useState} from 'react';
import {ActivityIndicator, TouchableOpacity} from 'react-native';
import Box from '@/Atoms/Box';
import Card from '@/Atoms/Card';
import CCText from '@/Atoms/CCText';
import Ionicons from 'react-native-vector-icons/Ionicons';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import type {
  ChapterProgressRoster,
  ChapterProgressRosterRow,
} from '@/Hooks/StudyChapterProgress/useGetChapterProgressRoster';

/**
 * How the coach reads an Exercise.
 *
 * Results have been recorded per participant since practice mode shipped, but
 * nothing ever displayed them — `useGetChapterProgress` returns only the
 * requesting user's own row, so an assessed chapter was assessed into a void.
 * This is the missing read side: who solved it, on which attempt, and how
 * much help they needed.
 *
 * Owner-only, and only worth showing on an Exercise chapter — a Sandbox game
 * is nobody's business but the player's, and never reaches storage anyway.
 */

export type ExerciseResultsCardProps = {
  roster: ChapterProgressRoster | null | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
};

/** Best result first, since that's the number a coach actually acts on. */
function resultLabel(row: ChapterProgressRosterRow): {
  text: string;
  color: string;
  bg: string;
} {
  const best = row.best_result ?? row.result;
  switch (best) {
    case 'won':
      return {text: 'Solved', color: '#1B7F4B', bg: 'rgba(27,127,75,0.12)'};
    case 'drawn':
      return {text: 'Drew', color: '#8A6D1F', bg: 'rgba(138,109,31,0.14)'};
    case 'lost':
      return {text: 'Not yet', color: '#A4402F', bg: 'rgba(164,64,47,0.12)'};
    case 'ongoing':
      return {
        text: 'In progress',
        color: CCColors.Workbook.Brown,
        bg: updateColorWithOpacity(CCColors.Workbook.Brown, 0.1),
      };
    default:
      // Never opened it. Deliberately distinct from "tried and failed" - the
      // coach's response to the two is completely different.
      return {
        text: 'Not started',
        color: CCColors.Workbook.Grey,
        bg: 'transparent',
      };
  }
}

/** "3 attempts · 12 moves · 2 take backs", skipping anything unmeasured. */
function detailLine(row: ChapterProgressRosterRow): string {
  const parts: string[] = [];
  if (row.attempts != null) {
    parts.push(`${row.attempts} attempt${row.attempts === 1 ? '' : 's'}`);
  }
  if (row.move_count != null) {
    parts.push(`${row.move_count} moves`);
  }
  if (row.takebacks_used != null && row.takebacks_used > 0) {
    parts.push(
      `${row.takebacks_used} take back${row.takebacks_used === 1 ? '' : 's'}`,
    );
  }
  if (row.hint_used) {
    parts.push('used a hint');
  }
  return parts.join(' · ');
}

function Row({row, isLast}: {row: ChapterProgressRosterRow; isLast: boolean}) {
  const badge = resultLabel(row);
  const detail = detailLine(row);
  return (
    <Box
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingVertical: 9,
        borderBottomWidth: isLast ? 0 : 1,
        borderBottomColor: updateColorWithOpacity(CCColors.Workbook.Brown, 0.1),
      }}>
      <Box style={{flex: 1}}>
        <CCText fontSize={12.5} color={CCColors.TextColor.Primary}>
          {row.name || `Participant ${row.user_id}`}
        </CCText>
        {!!detail && (
          <CCText fontSize={10.5} color={CCColors.Workbook.Grey}>
            {detail}
          </CCText>
        )}
      </Box>
      <Box
        style={{
          paddingVertical: 3,
          paddingHorizontal: 8,
          borderRadius: 10,
          backgroundColor: badge.bg,
        }}>
        <CCText fontSize={10.5} fontWeight="bold" color={badge.color}>
          {badge.text}
        </CCText>
      </Box>
    </Box>
  );
}

export default function ExerciseResultsCard({
  roster,
  isLoading,
  isError,
  onRetry,
}: ExerciseResultsCardProps) {
  const [open, setOpen] = useState(false);
  const summary = roster
    ? `${roster.completed_count} of ${roster.participant_count} solved`
    : 'Participant results for this exercise.';

  return (
    <Card style={{padding: 12}}>
      <TouchableOpacity
        accessibilityRole="button"
        accessibilityLabel="Exercise results"
        onPress={() => setOpen(prev => !prev)}
        style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
        <Box
          style={{
            width: 26,
            height: 26,
            borderRadius: 13,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: CCColors.LightYellow,
          }}>
          <Ionicons
            name="people-outline"
            size={15}
            color={CCColors.Workbook.GoldText}
          />
        </Box>
        <Box style={{flex: 1}}>
          <CCText
            fontSize={13}
            fontWeight="bold"
            color={CCColors.TextColor.Primary}>
            Exercise results
          </CCText>
          <CCText fontSize={11} color={CCColors.Workbook.Grey}>
            {summary}
          </CCText>
        </Box>
        <Ionicons
          name={open ? 'chevron-up' : 'chevron-down'}
          size={15}
          color={CCColors.Workbook.Brown}
        />
      </TouchableOpacity>

      {open && (
        <Box style={{marginTop: 10}}>
          {isLoading && <ActivityIndicator color={CCColors.Workbook.Brown} />}
          {isError && (
            <TouchableOpacity onPress={onRetry}>
              <CCText fontSize={11.5} color={CCColors.Workbook.Brown}>
                Couldn’t load results. Tap to retry.
              </CCText>
            </TouchableOpacity>
          )}
          {!isLoading && !isError && !roster?.rows?.length && (
            <CCText fontSize={11.5} color={CCColors.Workbook.Grey}>
              No participants have been added to this workbook yet.
            </CCText>
          )}
          {!isLoading &&
            !isError &&
            roster?.rows?.map((row, idx) => (
              <Row
                key={row.user_id}
                row={row}
                isLast={idx === roster.rows.length - 1}
              />
            ))}
        </Box>
      )}
    </Card>
  );
}
